// Roster -> Time Off: blocked periods ("no leave over Christmas / New Year"). Drivers who try to
// request leave inside a block they're covered by are refused with the message entered here.
// Existing pending/approved leave inside a block is not touched -- it is flagged on the block.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { Ban, Pencil, Plus, Trash2 } from "lucide-react";
import {
  DEFAULT_BLOCK_MESSAGE,
  blockCoversDriver,
  blockOverlapsRequest,
  deleteTimeOffBlock,
  describeBlockAudience,
  formatBlockDates,
  loadTimeOffBlocks,
  saveTimeOffBlock,
  type BlockDriver,
  type TimeOffBlock,
} from "@/lib/timeOffBlocks";

interface DriverTypeOption {
  id: string;
  type_name: string;
}

interface OpenRequest {
  id: string;
  driver_name: string;
  request_type: "partial_day" | "full_day" | "multi_day";
  start_date: string;
  end_date: string;
  start_time: string | null;
  end_time: string | null;
  status: "pending" | "approved";
}

type FormState = {
  id?: string;
  name: string;
  start_date: string;
  end_date: string;
  limitHours: boolean;
  start_time: string;
  end_time: string;
  team_names: string[];
  driver_type_ids: string[];
  message: string;
  is_active: boolean;
};

const todayIso = () => new Date().toLocaleDateString("en-CA", { timeZone: "Pacific/Auckland" });

const emptyForm = (): FormState => ({
  name: "",
  start_date: "",
  end_date: "",
  limitHours: false,
  start_time: "17:00",
  end_time: "03:00",
  team_names: [],
  driver_type_ids: [],
  message: DEFAULT_BLOCK_MESSAGE,
  is_active: true,
});

const Chip: React.FC<{ on: boolean; onClick: () => void; children: React.ReactNode }> = ({ on, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
      on ? "bg-orange-600/30 border-orange-500 text-orange-200" : "bg-gray-800 border-gray-600 text-gray-300 hover:border-gray-500"
    }`}
    aria-pressed={on}
  >
    {children}
  </button>
);

const TimeOffBlockedPeriods: React.FC<{ canEdit?: boolean }> = ({ canEdit = true }) => {
  const [blocks, setBlocks] = useState<TimeOffBlock[]>([]);
  const [teams, setTeams] = useState<string[]>([]);
  const [types, setTypes] = useState<DriverTypeOption[]>([]);
  const [driversInfo, setDriversInfo] = useState<Record<string, BlockDriver>>({});
  const [openRequests, setOpenRequests] = useState<OpenRequest[]>([]);
  const [showPast, setShowPast] = useState(false);
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    const [b, groupsRes, typesRes, driversRes] = await Promise.all([
      loadTimeOffBlocks(),
      supabase.from("roster_driver_groups").select("name, sort_order, is_active").order("sort_order", { ascending: true }),
      supabase.from("driver_types").select("id, type_name").eq("is_active", true).order("type_name"),
      supabase.from("drivers").select("driver_name, roster_groups, driver_type_id"),
    ]);
    setBlocks(b);
    setTypes((typesRes.data as DriverTypeOption[]) || []);

    const info: Record<string, BlockDriver> = {};
    const seenGroups = new Set<string>();
    ((driversRes.data as any[]) || []).forEach((d) => {
      info[d.driver_name] = { groups: d.roster_groups ?? [], driverTypeId: d.driver_type_id ?? null };
      (d.roster_groups ?? []).forEach((g: string) => seenGroups.add(g));
    });
    setDriversInfo(info);

    // Teams: the roster groups set up in the roster, plus any a driver is in that aren't listed there.
    const listed = ((groupsRes.data as any[]) || []).filter((g) => g.is_active !== false).map((g) => g.name as string);
    setTeams([...listed, ...[...seenGroups].filter((g) => !listed.includes(g)).sort()]);

    // Leave that is already pending/approved inside a block's dates (to flag, never to change).
    if (b.length > 0) {
      const from = b.reduce((m, x) => (x.start_date < m ? x.start_date : m), b[0].start_date);
      const to = b.reduce((m, x) => (x.end_date > m ? x.end_date : m), b[0].end_date);
      const { data } = await supabase
        .from("roster_time_off_requests")
        .select("id, driver_name, request_type, start_date, end_date, start_time, end_time, status")
        .in("status", ["pending", "approved"])
        .lte("start_date", to)
        .gte("end_date", from);
      setOpenRequests((data as OpenRequest[]) || []);
    } else {
      setOpenRequests([]);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const typeNames = useMemo(() => Object.fromEntries(types.map((t) => [t.id, t.type_name])), [types]);

  /** Pending / approved requests that fall inside this block for drivers it covers. */
  const affected = (b: TimeOffBlock) => {
    const hits = openRequests.filter((r) => {
      const d = driversInfo[r.driver_name] ?? { groups: [], driverTypeId: null };
      return (
        blockCoversDriver(b, d) &&
        blockOverlapsRequest(b, {
          type: r.request_type,
          startDate: r.start_date,
          endDate: r.end_date,
          startTime: r.start_time,
          endTime: r.end_time,
        })
      );
    });
    return { pending: hits.filter((h) => h.status === "pending"), approved: hits.filter((h) => h.status === "approved") };
  };

  const today = todayIso();
  const upcoming = blocks.filter((b) => b.end_date.slice(0, 10) >= today);
  const past = blocks.filter((b) => b.end_date.slice(0, 10) < today);
  const shown = showPast ? [...upcoming, ...past] : upcoming;

  const openNew = () => setForm(emptyForm());
  const openEdit = (b: TimeOffBlock) =>
    setForm({
      id: b.id,
      name: b.name,
      start_date: b.start_date.slice(0, 10),
      end_date: b.end_date.slice(0, 10),
      limitHours: !!(b.start_time && b.end_time),
      start_time: b.start_time?.slice(0, 5) || "17:00",
      end_time: b.end_time?.slice(0, 5) || "03:00",
      team_names: b.team_names,
      driver_type_ids: b.driver_type_ids,
      message: b.message,
      is_active: b.is_active,
    });

  const toggle = (list: string[], value: string) => (list.includes(value) ? list.filter((x) => x !== value) : [...list, value]);

  const save = async () => {
    if (!form) return;
    if (!form.name.trim()) return toast({ title: "Give it a name", description: "e.g. Christmas / New Year", variant: "destructive" });
    if (!form.start_date || !form.end_date) return toast({ title: "Choose the dates", variant: "destructive" });
    if (form.end_date < form.start_date) return toast({ title: "The end date can't be before the start date", variant: "destructive" });
    if (!form.message.trim()) return toast({ title: "Enter the message drivers will see", variant: "destructive" });
    if (form.limitHours && (!form.start_time || !form.end_time)) return toast({ title: "Enter both times", variant: "destructive" });

    setSaving(true);
    try {
      await saveTimeOffBlock({
        id: form.id,
        name: form.name,
        start_date: form.start_date,
        end_date: form.end_date,
        start_time: form.limitHours ? form.start_time : null,
        end_time: form.limitHours ? form.end_time : null,
        team_names: form.team_names,
        driver_type_ids: form.driver_type_ids,
        message: form.message,
        is_active: form.is_active,
      });
      toast({ title: form.id ? "Blocked period updated" : "Dates blocked", description: form.name });
      setForm(null);
      await refresh();
    } catch (e: any) {
      toast({ title: "Couldn't save", description: e?.message ?? String(e), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (b: TimeOffBlock) => {
    if (!window.confirm(`Remove "${b.name}"? Drivers will be able to request leave on those dates again.`)) return;
    try {
      await deleteTimeOffBlock(b.id);
      await refresh();
    } catch (e: any) {
      toast({ title: "Couldn't remove", description: e?.message ?? String(e), variant: "destructive" });
    }
  };

  const flipActive = async (b: TimeOffBlock) => {
    try {
      await saveTimeOffBlock({ ...b, is_active: !b.is_active });
      await refresh();
    } catch (e: any) {
      toast({ title: "Couldn't update", description: e?.message ?? String(e), variant: "destructive" });
    }
  };

  return (
    <Card className="bg-gray-900/60 border-gray-700">
      <CardContent className="p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h4 className="text-base font-semibold text-white flex items-center gap-2">
              <Ban className="h-4 w-4 text-red-400" />
              Blocked dates for leave
            </h4>
            <p className="text-xs text-gray-400">
              Drivers can't request time off inside these dates. They see the message you write; leave you enter yourself isn't blocked.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {past.length > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setShowPast((v) => !v)}>
                {showPast ? "Hide past" : `Show past (${past.length})`}
              </Button>
            )}
            {canEdit && (
              <Button size="sm" onClick={openNew} className="bg-red-700 hover:bg-red-600">
                <Plus className="h-4 w-4 mr-1" /> Block dates
              </Button>
            )}
          </div>
        </div>

        {shown.length === 0 ? (
          <p className="text-sm text-gray-400">No dates are blocked. Use "Block dates" to stop leave requests, e.g. over Christmas / New Year.</p>
        ) : (
          <div className="space-y-2">
            {shown.map((b) => {
              const a = affected(b);
              const isPast = b.end_date.slice(0, 10) < today;
              return (
                <div
                  key={b.id}
                  className={`rounded-lg border p-3 ${b.is_active && !isPast ? "border-red-900/60 bg-red-950/20" : "border-gray-700 bg-gray-800/40 opacity-70"}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-white">{b.name}</span>
                        {!b.is_active && <Badge variant="outline" className="text-[10px]">Switched off</Badge>}
                        {isPast && <Badge variant="outline" className="text-[10px]">Past</Badge>}
                      </div>
                      <div className="text-sm text-gray-300">{formatBlockDates(b)}</div>
                      <div className="text-xs text-gray-400">Applies to: {describeBlockAudience(b, typeNames)}</div>
                      <div className="text-xs text-gray-300 mt-1 italic">"{b.message}"</div>
                      {(a.pending.length > 0 || a.approved.length > 0) && (
                        <div className="text-xs text-amber-300 mt-1">
                          Already requested inside this block (not changed):{" "}
                          {a.approved.length > 0 && <span>{a.approved.length} approved ({[...new Set(a.approved.map((r) => r.driver_name))].join(", ")})</span>}
                          {a.approved.length > 0 && a.pending.length > 0 && " · "}
                          {a.pending.length > 0 && <span>{a.pending.length} pending ({[...new Set(a.pending.map((r) => r.driver_name))].join(", ")})</span>}
                        </div>
                      )}
                    </div>
                    {canEdit && (
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => flipActive(b)}>
                          {b.is_active ? "Switch off" : "Switch on"}
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => openEdit(b)} aria-label="Edit">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => remove(b)} aria-label="Remove">
                          <Trash2 className="h-4 w-4 text-red-400" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <Dialog open={!!form} onOpenChange={(v) => !v && setForm(null)}>
        <DialogContent className="max-w-lg bg-gray-900 border-gray-700 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{form?.id ? "Edit blocked dates" : "Block dates for leave"}</DialogTitle>
          </DialogHeader>
          {form && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-gray-300">Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Christmas / New Year"
                  className="bg-gray-800 border-gray-600 mt-1"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-300">From</Label>
                  <Input
                    type="date"
                    value={form.start_date}
                    onChange={(e) => setForm({ ...form, start_date: e.target.value, end_date: form.end_date || e.target.value })}
                    className="bg-gray-800 border-gray-600 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-300">To (same day for one day)</Label>
                  <Input
                    type="date"
                    value={form.end_date}
                    min={form.start_date}
                    onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                    className="bg-gray-800 border-gray-600 mt-1"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={form.limitHours} onCheckedChange={(v) => setForm({ ...form, limitHours: !!v })} />
                  Only block certain hours each day
                </label>
                {form.limitHours && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-gray-300">From</Label>
                      <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="bg-gray-800 border-gray-600 mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-300">To (can be after midnight)</Label>
                      <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="bg-gray-800 border-gray-600 mt-1" />
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-gray-300">Applies to</Label>
                <p className="text-xs text-gray-400">
                  Pick one or more teams and/or driver types. A driver is covered if they're in any team picked or are any type picked.
                  {form.team_names.length === 0 && form.driver_type_ids.length === 0 ? " Nothing picked = everyone." : ""}
                </p>
                <div>
                  <div className="text-[11px] text-gray-400 mb-1">Teams</div>
                  <div className="flex flex-wrap gap-1.5">
                    {teams.map((t) => (
                      <Chip key={t} on={form.team_names.includes(t)} onClick={() => setForm({ ...form, team_names: toggle(form.team_names, t) })}>
                        {t}
                      </Chip>
                    ))}
                    {teams.length === 0 && <span className="text-xs text-gray-500">No teams set up</span>}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] text-gray-400 mb-1">Driver types</div>
                  <div className="flex flex-wrap gap-1.5">
                    {types.map((t) => (
                      <Chip key={t.id} on={form.driver_type_ids.includes(t.id)} onClick={() => setForm({ ...form, driver_type_ids: toggle(form.driver_type_ids, t.id) })}>
                        {t.type_name}
                      </Chip>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-gray-300">Message drivers will see</Label>
                <Textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="bg-gray-800 border-gray-600 mt-1 min-h-[90px]"
                />
              </div>

              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: !!v })} />
                Active (untick to keep it saved but not enforce it)
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>
              Cancel
            </Button>
            <Button onClick={save} disabled={saving} className="bg-red-700 hover:bg-red-600">
              {saving ? "Saving…" : form?.id ? "Save changes" : "Block these dates"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default TimeOffBlockedPeriods;
